import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './MobilePropertyDetail.css';

const MobilePropertyDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSnapshot = async () => {
      try {
        const res = await axios.get(`/api/properties/snapshots/public/${id}`);
        if (res.data && res.data.ok) {
          setData(res.data.data);
        }
      } catch (err) {
        console.error("스냅샷 로드 실패:", err);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchSnapshot();
  }, [id]);

  useEffect(() => {
    if (!data || !data.lat || !data.lng) return;

    const { kakao } = window;
    
    // kakao 객체 자체가 없으면 스크립트 로드 실패
    if (!kakao) {
      console.error("Kakao SDK missing");
      return;
    }

  // autoload=false 일 때는 반드시 load() 콜백 안에서 지도를 그려야 함
  kakao.maps.load(() => {
    const container = document.getElementById('map-canvas');
    if (!container) return;

    const lat = parseFloat(data.lat);
    const lng = parseFloat(data.lng);
    const position = new kakao.maps.LatLng(lat, lng);

    const map = new kakao.maps.Map(container, {
      center: position,
      level: 3
    });

    new kakao.maps.Marker({ position }).setMap(map);

    // 모바일 렌더링 지연 대응
    setTimeout(() => {
      map.relayout();
      map.setCenter(position);
    }, 500);
  });
}, [data]);

  if (loading) return <div className="loading">매물 정보를 불러오는 중...</div>;
  if (!data) return <div className="error">정보를 찾을 수 없습니다.</div>;

  // 1. PC 스크롤을 위해 컨테이너 스타일 보강
  const containerStyle = {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    minHeight: '100vh',
    backgroundColor: '#fff',
    overflowY: 'auto', // ✅ 스크롤 허용
    paddingBottom: '50px'
  };

  // 상세 사양 섹션에 표시할 값이 하나라도 있는지 체크
  const hasDetailInfo = data && (
     data.exclusive_scale || 
     data.floor || 
     data.room_cnt || 
     data.parking_cnt !== undefined || 
     data.completion_year || 
     data.estate_type
  );

  return (
    <div className="mobile-container" style={containerStyle}>
      {/* 1. 지도 섹션 (소재지 미전송 시 숨김) */}
      {data.lat && data.lng && (
        <section className="map-section">
          <div id="map-canvas" style={{ width: '100%', height: '250px' }}></div>
          <div className="map-btn-wrapper">
            <a 
              href={`https://map.kakao.com/link/map/${encodeURIComponent(data.estate_name || '매물위치')},${data.lat},${data.lng}`}
              target="_blank" 
              rel="noreferrer"
              className="map-btn"
            >
              📍 카카오맵에서 위치 보기
            </a>
          </div>
        </section>
      )}

      {/* 2. 건물명 및 가격 섹션 */}
      <section className="info-section">
        <h2 className="title">{data.estate_name || '매물 정보'}</h2>
        <div className="price-tag">{data.price_info}</div>

        {(data.roadAddress || data.jibunAddress) && (
          <div className="address-box">
            {data.roadAddress && <p><strong>도로명</strong> {data.roadAddress}</p>}
            {data.jibunAddress && <p><strong>지번</strong> {data.jibunAddress}</p>}
          </div>
        )}
      </section>

      {/* 3. 상세 사양 섹션 (체크한 항목만 출력) */}
      {hasDetailInfo && (
        <section className="info-section border-top">
          <h3 className="section-title">📋 상세 정보</h3>
          <table className="info-table">
            <tbody>
              {/* ✅ 금액 정보 추가 */}
              {data.price_info && (
                <tr>
                  <th>거래금액</th>
                  <td style={{ color: '#d32f2f', fontWeight: 'bold' }}>{data.price_info}</td>
                </tr>
              )}
              {data.estate_type && (
                <tr><th>매물종류</th><td>{data.estate_type}</td></tr>
              )}
              {data.exclusive_scale && (
                <tr><th>전용면적</th><td>{data.exclusive_scale}㎡</td></tr>
              )}
              {/* ✅ 해당호수(및 동) 출력 추가 */}
              {(data.estate_dong || data.estate_ho) && (
                <tr>
                  <th>해당호수</th>
                  <td>
                    {data.estate_dong ? `${data.estate_dong} ` : ""}
                    {data.estate_ho ? (String(data.estate_ho).includes('호') ? data.estate_ho : `${data.estate_ho}호`) : ""}
                  </td>
                </tr>
              )}
              {data.floor && (
                <tr><th>해당층</th><td>{data.floor}층</td></tr>
              )}
              {(data.room_cnt || data.bath_cnt) && (
                <tr><th>룸/욕실</th>
                  <td>
                    {data.room_cnt ? `${data.room_cnt}룸` : ""} 
                    {data.room_cnt && data.bath_cnt ? " / " : ""}
                    {data.bath_cnt ? (String(data.bath_cnt).includes("욕실") ? data.bath_cnt : `욕실 ${data.bath_cnt}`) : ""}
                  </td>
                </tr>
              )}
              {data.parking_cnt !== undefined && (
                <tr><th>주차정보</th><td>{Number(data.parking_cnt) > 0 ? `가능(${data.parking_cnt}대)` : '불가능'}</td></tr>
              )}
              {data.completion_year && (
                <tr><th>준공일</th><td>{data.completion_year.substring(0, 10)}</td></tr>
              )}
            </tbody>
          </table>
        </section>
      )}

      {/* ✨ 시설 상태 섹션 보강 */}
      {data.facilities && data.facilities.length > 0 && (
        <section className="info-section border-top">
          <h3 className="section-title">✨ 주요 시설</h3>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px', 
            padding: '10px 0' 
          }}>
            {data.facilities.map((f, i) => (
              <span key={i} style={{
                background: '#f1f7ff',
                color: '#3182ce',
                padding: '5px 12px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600',
                border: '1px solid #c0d9ff'
              }}>
                {f}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* 6. 사진 섹션 */}
      {data.photos && (Array.isArray(data.photos) ? data.photos : JSON.parse(data.photos || "[]")).length > 0 && (
        <section className="photo-section border-top">
          {(Array.isArray(data.photos) ? data.photos : JSON.parse(data.photos || "[]")).map((url, idx) => (
            <img 
              key={idx} 
              src={url.replace('sz=w200', 'sz=w600')} // 썸네일을 큰 이미지로 변경
              className="full-width-img" 
              alt={`매물사진 ${idx + 1}`} 
              style={{ width: '100%', marginBottom: '10px', display: 'block' }}
            />
          ))}
        </section>
      )}
    </div>
  );
};

export default MobilePropertyDetail;